import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Grid, Segment } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { setCloseMonitoring } from "../../actions";
import { isSystemAdmin, isSeniorAdministrator } from "../../../../auth/Authorize";
import { EmployeeRecordDetailModel } from "../../model";

import { ApplicationState } from "../../../../applicationState";

import { CloseMonitoringChangeConfirmModal } from "./CloseMonitoringConfirmModal";

interface Props {
    employeeRecord: EmployeeRecordDetailModel;
    customerId: string;
}

const CloseMonitoringLabel = "Close Monitoring";

export const CloseMonitoringSection: React.FunctionComponent<Props> = (props: Props) => {
    const [closeMonitoringModalOpen, setCloseMonitoringModalOpen] = React.useState<boolean>(false);
    const [changingCloseMonitoring, setChangingCloseMonitoring] = React.useState<boolean>(false);

    const dispatch = useDispatch();
    const currentUser = useSelector((state: ApplicationState) => state.currentUser);

    const isChangingCloseMonitoringAllowed = React.useMemo(() =>
        isSystemAdmin(currentUser) || (isSeniorAdministrator(currentUser) && !props.employeeRecord?.closeMonitoring)
    , [currentUser, props.employeeRecord?.closeMonitoring]);

    const onOhCaseCloseMonitoringChangeConfirm = React.useCallback(async () => {
        if (isChangingCloseMonitoringAllowed && props.customerId && props.employeeRecord?.id) {
            setChangingCloseMonitoring(true);
            await dispatch(setCloseMonitoring(props.customerId, props.employeeRecord.id, !props.employeeRecord.closeMonitoring));
            setChangingCloseMonitoring(false);
            setCloseMonitoringModalOpen(false);
        }
    }, [dispatch, isChangingCloseMonitoringAllowed, props.customerId, props.employeeRecord.id, props.employeeRecord.closeMonitoring]);

    const openCloseMonitoringModal = React.useCallback(() => {
        setCloseMonitoringModalOpen(true);
    }, []);

    const onCloseMonitoringModalClosed = React.useCallback(() => {
        setCloseMonitoringModalOpen(false);
    }, []);

    if (isChangingCloseMonitoringAllowed) {
        return (
            <>
                <Grid.Row>
                    <Segment loading={changingCloseMonitoring} basic>
                        <Input.Checkbox
                            label={CloseMonitoringLabel}
                            value={props.employeeRecord?.closeMonitoring}
                            disabled
                        />
                    </Segment>
                </Grid.Row>
                <Grid.Row>
                    <Button
                        content="Change Close Monitoring"
                        disabled={!isChangingCloseMonitoringAllowed || changingCloseMonitoring}
                        loading={changingCloseMonitoring}
                        onClick={openCloseMonitoringModal}
                        size="small"
                    />
                </Grid.Row>
                <CloseMonitoringChangeConfirmModal
                    isOpen={closeMonitoringModalOpen}
                    onClose={onCloseMonitoringModalClosed}
                    employeeFirstName={props.employeeRecord?.firstName || ""}
                    employeeLastName={props.employeeRecord?.lastName || ""}
                    onConfirm={onOhCaseCloseMonitoringChangeConfirm}
                />
            </>
        );
    }

    return (
        <Segment loading={changingCloseMonitoring} basic>
            <Input.Checkbox
                label={CloseMonitoringLabel}
                value={props.employeeRecord?.closeMonitoring}
                disabled
            />
        </Segment>
    );
};
